import React, { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import { CDN2 } from '@/helpers/api';
import { Breadcrumb } from 'antd';
import CardCoupons from '@/components/CardCoupons';
import ListCoupons from '@/components/ListCoupons';
import FAQCouponsPage from '@/components/FAQCouponsPage';
import { GetServerSideProps } from 'next';
import { basicPageProps, mobileProps } from '@/utils/isServer';
import Search from '@/components2/Search';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { isMobile, isIos } = mobileProps(ctx);
  const { airports, configInfo } = await basicPageProps(ctx);

  return {
    props: {
      airports,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
      mobileType: isIos ? 'ios' : 'android',
    },
  };
};

function ParkingCoupons({ airports, configInfo, deviceType }) {
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'On Air Parking',
    url: 'https://www.onairparking.com/',
    logo: CDN2() + '/staticmyapp/oglogo-white.png',
    description:
      'Unlock exclusive airport parking coupons and save on your parking reservations.',
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.onairparking.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Parking Coupons',
        item: 'https://www.onairparking.com/parking-coupons',
      },
    ],
  };

  return (
    <div className="relative">
      <Head>
        <title>
          Airport Parking Coupons | Save on Parking Reservations – On Air
          Parking
        </title>
        <meta
          name="description"
          content="Unlock exclusive airport parking coupons to save on reservations. Book your parking spot online at On Air Parking."
        />
        <link rel="canonical" href="https://onairparking.com/parking-coupons" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Airport Parking Coupons | Save on Parking Reservations – On Air Parking"
        />
        <meta
          property="og:description"
          content="Unlock exclusive discounts on airport parking reservations. Book your parking spot online and save!"
        />
        <meta
          property="og:url"
          content={`https://www.onairparking.com/parking-coupons/`}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaData),
          }}
        ></script>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbData),
          }}
        ></script>
      </Head>
      <div className="md:min-h-full relative">
        <div className="relative" style={{ backgroundColor: '#d0a785' }}>
          <Image
            src={CDN2() + '/staticmyapp/bg-old-landing-desktop.webp'}
            className="z-0 w-full object-cover min-h-64 h-full absolute object-bottom"
            alt="Airport Background"
            fill
            priority
            quality={70}
          />

          <div className="container mx-auto px-4 py-6 z-10 relative">
            <div
              className="flex flex-col justify-center text-center text-gray-600 mb-4"
              suppressHydrationWarning
            >
              <h1
                className={`text-2xl lg:text-4xl w-full text-center mb-4 font-bold text-gray-600 uppercase`}
              >
                Airport Parking Coupons
              </h1>
              <p className="text-base md:text-lg">
                Welcome to On Air Parking! Discover exclusive coupons and
                discounts to make your airport parking reservations easier and
                more affordable.
              </p>
            </div>

            <Search searchType="airport" airports={airports} deviceType={deviceType} />
          </div>
        </div>
        <div className="container mx-auto px-4 py-6 z-10 relative">
          <Breadcrumb className="text-sm md:text-base mt-4 mb-8">
            <Breadcrumb.Item>
              <a href="/">Home</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Parking Coupons</Breadcrumb.Item>
          </Breadcrumb>

          <section
            className="flex flex-row justify-between items-center gap-2 bg-orange-50 p-6 md:p-20 mt-8 relative overflow-hidden"
            id="about-coupons"
          >
            <div className="flex-1 space-y-4 md:pr-20 lg:pr-40">
              <h2 className="text-xl lg:text-3xl font-bold">
                ABOUT ON AIR PARKING COUPONS
              </h2>

              <p className="text-lg">
                At On Air Parking, we aim to make airport parking simple,
                convenient, and budget-friendly. Partnering with trusted
                facilities nationwide, we offer a variety of options to suit
                your needs, including covered and uncovered parking, valet
                service, and self-parking. Rest assured, you're securing the
                best deal for your trip.
              </p>

              <p className="text-lg">
                Looking to save even more? Simply choose one of the available
                coupons below, copy the code, and apply it at checkout to claim
                your savings.
              </p>

              <p className="text-lg">
                Below is a complete list of available coupons to help you save
                on your next parking reservation:
              </p>

              <ul className="list-disc pl-5">
                <li>
                  <b>App Booking</b>: Save up to $
                  {configInfo.couponPageAppDiscount} when using the On Air
                  Parking app. Use the coupon code at checkout when booking
                  through the app.
                </li>
                <li>
                  <b>Website Booking</b>: Save $
                  {configInfo.couponPageWebDiscount} when booking on our
                  website. Use the coupon code at checkout when booking
                  through the website.
                </li>
              </ul>

              <p className="text-lg font-bold">
                Please note: You must be logged in to your account to apply the
                coupon code. Each coupon can be used one time per customer,
                either on the app or the website. Don't miss out—choose the
                coupon that works best for you and start saving!
              </p>
            </div>
            <div className="hidden md:flex absolute -right-28 md:-right-40 w-56 h-56 md:w-80 md:h-80 rounded-full overflow-hidden">
              <Image
                src={`${CDN2()}/staticmyapp/coupons/coupons.jpg`}
                fill
                alt="Event Background"
                loading="lazy"
              />
            </div>
          </section>

          <section className="flex flex-col p-6 mt-8" id="top-airportss">
            <h2 className="text-xl lg:text-3xl font-bold">
              TOP ON AIR PARKING COUPONS
            </h2>
            <p className="text-lg mt-4">
              Exclusive discounts and special offers for airport parking to help
              you save more on your next trip.
            </p>
            {configInfo.couponPageSuffix &&
              configInfo.couponPageWebDiscount &&
              configInfo.couponPageAppDiscount && (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 mt-8">
                    <CardCoupons airports={airports} configInfo={configInfo} />
                  </div>
                  <div className="flex flex-col mt-8">
                    <h2 className="text-xl lg:text-3xl font-bold uppercase">
                      Coupon codes for all American Airports
                    </h2>
                    <ListCoupons airports={airports} />
                  </div>
                </>
              )}
            <div className="flex flex-col md:flex-row gap-6 md:gap-10 lg:gap-20 mt-8">
              <h2 className="font-bold uppercase text-xl lg:text-5xl lg:w-1/4 self-center">
                Frequently Asked Questions
              </h2>
              <FAQCouponsPage
                discountApp={configInfo.couponPageAppDiscount}
                discountWeb={configInfo.couponPageWebDiscount}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ParkingCoupons;
